import React, { useState, useEffect } from "react";
import axios from "../../utils/axios";
import Config from "../../utils/config";
import Modal from "react-modal";
import { useForm, Controller } from "react-hook-form";
import { ToastContainer, toast } from "react-toastify";
import Spinner from "../../components/Spinner";
import { Menu, MenuItem, IconButton } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "600px",
    width: "90%",
    maxHeight: "90vh",
    padding: 0,
    overflow: "hidden",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    border: "1px solid #ccc",
    borderRadius: "8px",
    zIndex: 10000,
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 9999,
  },
};

const Agent = () => {
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();

  const postes = [
    "ADS",
    "SSIAP 1",
    "SSIAP 2",
    "Maitre Chien",
    "Opérateurs Rayons X",
  ];

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${Config.baseURL}/agent`);
        setAgents(response.data);
      } catch (error) {
        console.error("Error fetching agents data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  const handleSearchInputChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleOpenModal = () => {
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    setSelectedAgent(null);
    reset({
      firstName: "",
      lastName: "",
      email: "",
      telephone: "",
      poste: "",
      matricules: "",
    });
  };

  const handleAddAgent = async (formData) => {
    if (!formData.matricules) {
      formData.matricules = "N/A";
    }
    try {
      setLoading(true);
      const response = await axios.post(`${Config.baseURL}/agent`, formData);
      setAgents([...agents, response.data]);
      handleCloseModal();
      toast.success("Nouvel agent ajouté avec succès");
    } catch (error) {
      console.error("Error adding new agent:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAgent = async (formData) => {
    try {
      setLoading(true);
      await axios.put(`${Config.baseURL}/agent/${selectedAgent._id}`, formData);
      const updatedAgents = agents.map((agent) =>
        agent._id === selectedAgent._id ? { ...agent, ...formData } : agent
      );
      setAgents(updatedAgents);
      handleCloseModal();
      toast.success("Agent mis à jour avec succès");
    } catch (error) {
      console.error("Error updating agent:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = agents.filter(
    (item) =>
      item.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.telephone.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.poste.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer cet agent ?`)) {
      try {
        await axios.delete(`${Config.baseURL}/agent/${id}`);
        const updatedAgents = agents.filter((agent) => agent._id !== id);
        setAgents(updatedAgents);
        toast.success("Agent supprimé avec succès");
      } catch (error) {
        console.error("Error deleting agent:", error);
        toast.error("Erreur lors de la suppression de l'agent");
      }
    }
  };

  const handleMenuClick = (event, agentId) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgentId(agentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgentId(null);
  };

  const handleEdit = (agent) => {
    setSelectedAgent(agent);
    reset(agent);
    handleOpenModal();
    handleMenuClose();
  };

  const handleDeleteFromMenu = (id) => {
    handleDelete(id);
    handleMenuClose();
  };

  const handleSendAvailabilityReminder = async (agent) => {
    setEmailSending(true);
    try {
      await axios.post(`${Config.baseURL}/emails/reminder-availability`, { email: agent.email });
      toast.success(`Rappel envoyé à ${agent.firstName} ${agent.lastName} pour remplir ses disponibilités.`);
    } catch (error) {
      console.error("Error sending availability reminder:", error);
      toast.error("Erreur lors de l'envoi du rappel de disponibilités.");
    } finally {
      setEmailSending(false);
      handleMenuClose();
    }
  };

  const handleSendWorkingHoursReminder = async (agent) => {
    setEmailSending(true);
    try {
      await axios.post(`${Config.baseURL}/emails/reminder-working-hours`, { email: agent.email });
      toast.success(`Rappel envoyé à ${agent.firstName} ${agent.lastName} pour remplir ses heures de travail.`);
    } catch (error) {
      console.error("Error sending working hours reminder:", error);
      toast.error("Erreur lors de l'envoi du rappel des heures de travail.");
    } finally {
      setEmailSending(false);
      handleMenuClose();
    }
  };

  const getFrenchMonth = (date) => {
    const months = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    return months[date.getMonth()];
  };

  const today = new Date();
  const dayOfMonth = today.getDate();
  let displayDate;
  if (dayOfMonth <= 15) {
    displayDate = today;
  } else {
    displayDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }
  const monthName = getFrenchMonth(displayDate);
  const year = displayDate.getFullYear();

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="overflow-x-hidden w-full min-w-0 max-w-full">
      {emailSending && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Spinner />
        </div>
      )}

      <header className="px-2 sm:px-6 md:px-10 mt-6 sm:mt-20 max-w-full">
        <h1 className="text-xl sm:text-2xl font-bold mb-4">Liste des agents</h1>
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="inline-flex items-center px-4 py-2 text-sm sm:text-lg font-medium text-white bg-blue-500 rounded-full shadow-lg">
            <span>
              Affichage pour le mois de {monthName} {year}
            </span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="relative flex-1 min-w-0 w-full">
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              className="pl-10 pr-4 py-2.5 sm:py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 min-h-[44px] sm:min-h-0"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
            </div>
          </div>
          <button
            type="button"
            className="w-full sm:w-auto sm:ml-4 bg-blue-500 text-white px-4 py-3 sm:py-2 rounded-md flex items-center justify-center min-h-[44px] sm:min-h-0"
            onClick={handleOpenModal}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="h-5 w-5 mr-2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.75v14.5m7.25-7.25H4.75"
              />
            </svg>
            Ajouter Agent
          </button>
        </div>
      </header>

      <div className="flex flex-col mt-6 sm:mt-10 mx-2 sm:mx-4 md:mx-5 max-w-full w-full min-w-0">
        <div className="rounded-lg border border-gray-200 bg-white shadow w-full min-w-0 max-w-full">
          <div className="overflow-y-auto overflow-x-auto max-h-[calc(100vh-220px)] w-full min-w-0" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="w-full border-collapse min-w-[640px]">
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead className={`bg-gray-50 shadow-sm ${!modalIsOpen ? "sticky top-0 z-10" : ""}`}>
                <tr>
                  <th className="min-w-0 px-2 py-2 sm:py-3 text-xs sm:text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Prénom
                  </th>
                  <th className="min-w-0 px-2 py-2 sm:py-3 text-xs sm:text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Nom
                  </th>
                  <th className="min-w-0 px-2 py-2 sm:py-3 text-xs sm:text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Email
                  </th>
                  <th className="min-w-0 px-2 py-2 sm:py-3 text-xs sm:text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Téléphone
                  </th>
                  <th className="min-w-0 px-2 py-2 sm:py-3 text-xs sm:text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Poste
                  </th>
                  <th className="min-w-0 px-2 py-2 sm:py-3 text-xs sm:text-sm text-left font-semibold text-gray-700 border-b border-gray-200">
                    Disponibilité
                  </th>
                  <th className="px-2 py-2 sm:py-3 text-xs sm:text-sm text-center font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap bg-gray-50 w-[16%] min-w-[72px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
              {filteredData.map((agent, idx) => (
                <tr key={agent._id} className={idx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50/50 hover:bg-gray-50"}>
                  <td className="px-2 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-800 truncate max-w-0" title={agent.firstName}>
                    {agent.firstName}
                  </td>
                  <td className="px-2 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-800 truncate max-w-0" title={agent.lastName}>
                    {agent.lastName}
                  </td>
                  <td className="px-2 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-800 truncate max-w-0" title={agent.email}>
                    {agent.email}
                  </td>
                  <td className="px-2 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-800 truncate max-w-0" title={agent.telephone}>
                    {agent.telephone}
                  </td>
                  <td className="px-2 py-2 sm:py-3 text-xs sm:text-sm font-medium text-gray-800 truncate max-w-0" title={agent.poste}>
                    {agent.poste}
                  </td>
                  <td className="px-2 py-2 sm:py-3 text-sm min-w-0">
                    {agent.isFilledThisMonth ? (
                      <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                        Remplie
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">
                        En Attente
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 sm:py-3 align-middle bg-gray-50">
                    <div className="flex justify-center items-center w-full">
                      <IconButton
                        size="small"
                        onClick={(event) => handleMenuClick(event, agent._id)}
                        className="min-h-[36px] min-w-[36px]"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedAgentId === agent._id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => handleEdit(agent)}>
                        Modifier
                      </MenuItem>
                      <MenuItem onClick={() => handleDeleteFromMenu(agent._id)}>
                        Supprimer
                      </MenuItem>
                      <MenuItem onClick={() => handleSendAvailabilityReminder(agent)}>
                        Rappel: Remplir Disponibilités
                      </MenuItem>
                      <MenuItem onClick={() => handleSendWorkingHoursReminder(agent)}>
                        Rappel: Remplir Heures de Travail
                      </MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Formulaire Agent"
        style={customStyles}
        ariaHideApp={false}
      >
        <div className="p-4 sm:p-6 overflow-x-hidden overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl sm:text-2xl font-bold mb-4">
          {selectedAgent ? "Modifier Agent" : "Ajouter Agent"}
        </h2>
        <form
          onSubmit={handleSubmit(
            selectedAgent ? handleUpdateAgent : handleAddAgent
          )}
        >
          <div className="mb-4">
            <label
              htmlFor="firstName"
              className="block text-md font-medium text-gray-700"
            >
              Prénom <span className="text-red-500">*</span>
            </label>
            <Controller
              name="firstName"
              control={control}
              defaultValue=""
              rules={{ required: "Prénom est requis" }}
              render={({ field }) => (
                <input
                  type="text"
                  id="firstName"
                  {...field}
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm mt-1">
                {errors.firstName.message}
              </p>
            )}
          </div>
          <div className="mb-4">
            <label
              htmlFor="lastName"
              className="block text-md font-medium text-gray-700"
            >
              Nom <span className="text-red-500">*</span>
            </label>
            <Controller
              name="lastName"
              control={control}
              defaultValue=""
              rules={{ required: "Nom est requis" }}
              render={({ field }) => (
                <input
                  type="text"
                  id="lastName"
                  {...field}
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm mt-1">
                {errors.lastName.message}
              </p>
            )}
          </div>
          <div className="mb-4">
            <label
              htmlFor="email"
              className="block text-md font-medium text-gray-700"
            >
              Email <span className="text-red-500">*</span>
            </label>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              rules={{
                required: "Email est requis",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: "Adresse email invalide",
                },
              }}
              render={({ field }) => (
                <input
                  type="email"
                  id="email"
                  {...field}
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="mb-4">
            <label
              htmlFor="telephone"
              className="block text-md font-medium text-gray-700"
            >
              Téléphone <span className="text-red-500">*</span>
            </label>
            <Controller
              name="telephone"
              control={control}
              defaultValue=""
              rules={{ required: "Téléphone est requis" }}
              render={({ field }) => (
                <input
                  type="text"
                  id="telephone"
                  {...field}
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            />
            {errors.telephone && (
              <p className="text-red-500 text-sm mt-1">
                {errors.telephone.message}
              </p>
            )}
          </div>
          <div className="mb-4">
            <label
              htmlFor="poste"
              className="block text-md font-medium text-gray-700"
            >
              Poste <span className="text-red-500">*</span>
            </label>
            <Controller
              name="poste"
              control={control}
              defaultValue="ADS"
              render={({ field }) => (
                <select
                  id="poste"
                  {...field}
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  {postes.map((poste) => (
                    <option key={poste} value={poste}>
                      {poste}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.poste && (
              <p className="text-red-500 text-sm mt-1">{errors.poste.message}</p>
            )}
          </div>
          <div className="mb-4">
            <label
              htmlFor="matricules"
              className="block text-md font-medium text-gray-700"
            >
              Matricules
            </label>
            <Controller
              name="matricules"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <input
                  type="text"
                  id="matricules"
                  {...field}
                  className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-0 sm:gap-2">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-3 sm:py-2 rounded-md sm:mr-2 min-h-[44px] sm:min-h-0"
              onClick={handleCloseModal}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`bg-blue-500 text-white px-4 py-3 sm:py-2 rounded-md min-h-[44px] sm:min-h-0 ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSubmitting}
            >
              {selectedAgent ? "Mettre à jour" : "Ajouter"}
            </button>
          </div>
        </form>
        </div>
      </Modal>
      <ToastContainer />
    </main>
  );
};

export default Agent;
